<template>
  <div class="contact-page">
    <div class="contact-page__inner">

      <h1 class="contact-page__title">צרו איתנו קשר</h1>
      <p class="contact-page__intro">
        שלחו לנו הודעה ונציג שלנו יחזור אליכם בהקדם.
      </p>

      <!-- Inline success/error banner -->
      <div
        v-if="banner.message"
        class="contact-page__banner"
        :class="banner.type === 'error' ? 'contact-page__banner--error' : 'contact-page__banner--success'"
        role="alert"
        aria-live="assertive"
      >
        <svg class="contact-page__banner-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <template v-if="banner.type === 'error'">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </template>
          <template v-else>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </template>
        </svg>
        <span>{{ banner.message }}</span>
      </div>

      <form class="contact-page__form" @submit.prevent="contact_do" novalidate>

        <!-- Email -->
        <ui-input
          v-model="info.email"
          type="email"
          label="כתובת מייל"
          name="email"
          autocomplete="email"
          :required="true"
          :error="errors.email"
        />

        <!-- Name + Phone row -->
        <div class="contact-page__row">
          <ui-input
            v-model="info.name"
            type="text"
            label="שם מלא"
            name="name"
            autocomplete="name"
            :required="true"
            :error="errors.name"
          />
          <ui-input
            v-model="info.phone"
            type="tel"
            label="טלפון"
            name="phone"
            autocomplete="tel"
            :required="true"
            :error="errors.phone"
          />
        </div>

        <!-- Subject -->
        <ui-input
          v-model="info.subject"
          type="text"
          label="נושא"
          name="subject"
          :required="true"
          :error="errors.subject"
        />

        <!-- Message body — native textarea styled with KINO tokens -->
        <div class="contact-page__field-wrap">
          <label for="contact-content" class="contact-page__label">תוכן</label>
          <textarea
            id="contact-content"
            v-model="info.content"
            name="content"
            class="contact-page__textarea"
            :class="{ 'contact-page__textarea--error': errors.content }"
            rows="5"
            required
            aria-required="true"
            :aria-invalid="errors.content ? 'true' : undefined"
            :aria-describedby="errors.content ? 'contact-content-error' : undefined"
          ></textarea>
          <p v-if="errors.content" id="contact-content-error" class="contact-page__field-error">
            {{ errors.content }}
          </p>
        </div>

        <!-- Submit -->
        <div class="contact-page__submit">
          <ui-button
            type="submit"
            variant="primary"
            size="lg"
            :loading="submitting"
            :block="true"
          >
            שלח
          </ui-button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import UiInput from './ui/Input.vue';
import UiButton from './ui/Button.vue';

export default {
  components: { UiInput, UiButton },

  data() {
    return {
      submitting: false,
      info: {
        name: '',
        phone: '',
        email: '',
        subject: '',
        content: '',
      },
      errors: {
        name: '',
        phone: '',
        email: '',
        subject: '',
        content: '',
      },
      banner: {
        type: '',    // 'success' | 'error'
        message: '',
      },
    };
  },

  methods: {
    validate() {
      let ok = true;
      this.errors = { name: '', phone: '', email: '', subject: '', content: '' };

      if (!this.info.email.trim()) {
        this.errors.email = 'נדרשת כתובת מייל';
        ok = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.info.email)) {
        this.errors.email = 'כתובת המייל אינה תקינה';
        ok = false;
      }
      if (!this.info.name.trim()) {
        this.errors.name = 'נדרש שם מלא';
        ok = false;
      }
      if (!this.info.phone.trim()) {
        this.errors.phone = 'נדרש מספר טלפון';
        ok = false;
      }
      if (!this.info.subject.trim()) {
        this.errors.subject = 'נדרש נושא';
        ok = false;
      }
      if (!this.info.content.trim()) {
        this.errors.content = 'נדרש תוכן ההודעה';
        ok = false;
      }

      return ok;
    },

    contact_do() {
      this.banner = { type: '', message: '' };

      if (!this.validate()) return;

      this.submitting = true;

      this.api_call({
        url: 'contact',
        data: this.info,
        onComplete: () => {
          this.submitting = false;
          this.banner = {
            type: 'success',
            message: 'הודעתך נשלחה בהצלחה! נציג שלנו יחזור אליך בהקדם.',
          };
          this.info = { name: '', phone: '', email: '', subject: '', content: '' };
        },
        onError: () => {
          this.submitting = false;
          this.banner = {
            type: 'error',
            message: 'שליחת ההודעה נכשלה. נסו שנית מאוחר יותר.',
          };
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
/* ── Page wrapper ── */
.contact-page {
  background: var(--color-bg);
  min-height: 100vh;
  padding-block-start: var(--space-8);
  padding-block-end: var(--space-16);
  display: flex;
  justify-content: center;
}
:deep(.ui-input__native){
  border:0;
}

/* ── Centered panel ── */
.contact-page__inner {
  width: 100%;
  max-width: 560px;
  padding-inline: var(--space-6);
}

/* ── Title — title/lg §1.2 ── */
.contact-page__title {
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: -0.4px;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  margin-block-start: 0;
  margin-block-end: var(--space-2);
  text-align: start;
}

/* ── Intro — body/lg §1.2 ── */
.contact-page__intro {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--color-textMuted);
  font-family: var(--font-hebrew);
  margin-block-start: 0;
  margin-block-end: var(--space-8);
  text-align: start;
}

/* ── Inline banner — spec §2.7 Banner/Inline Alert ── */
.contact-page__banner {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: 14px 20px;
  border-radius: var(--radius-md);
  margin-block-end: var(--space-6);
  font-size: 14px;
  font-weight: 600;
  font-family: var(--font-hebrew);
  line-height: 1.4;
}

.contact-page__banner--success {
  background: rgba(31, 174, 107, 0.12);
  border: 1px solid rgba(31, 174, 107, 0.33);
  color: var(--color-success);
}

.contact-page__banner--error {
  background: rgba(224, 65, 65, 0.12);
  border: 1px solid rgba(224, 65, 65, 0.33);
  color: var(--color-danger);
}

.contact-page__banner-icon {
  flex-shrink: 0;
  margin-block-start: 1px;
}

/* ── Form ── */
.contact-page__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

/* ── Two-column row ── */
.contact-page__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

/* ── Textarea field wrapper ── */
.contact-page__field-wrap {
  display: flex;
  flex-direction: column;
}

/* ── Textarea label — matches ui/Input.vue label ── */
.contact-page__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-textMuted);
  font-family: var(--font-hebrew);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-block-end: var(--space-2);
}

/* ── Textarea — spec §2.2 Textarea ── */
.contact-page__textarea {
  min-height: 120px;
  padding: 14px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: 15px;
  font-family: var(--font-hebrew);
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color var(--duration-fast) var(--easing-default),
              box-shadow var(--duration-fast) var(--easing-default);
  caret-color: var(--color-text);
}

.contact-page__textarea::placeholder {
  color: var(--color-textDim);
}

.contact-page__textarea:focus {
  border-color: var(--color-text);
  box-shadow: var(--shadow-glow);
}

.contact-page__textarea--error {
  border-color: var(--color-danger);
}

.contact-page__textarea--error:focus {
  box-shadow: none;
}

/* ── Inline field error ── */
.contact-page__field-error {
  margin: 0;
  margin-block-start: 6px;
  font-size: 12px;
  color: var(--color-danger);
  font-family: var(--font-hebrew);
  line-height: 1.33;
}

/* ── Submit row ── */
.contact-page__submit {
  padding-block-start: var(--space-2);
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .contact-page__textarea {
    transition: none;
  }
}

/* ── Mobile ── */
@media (max-width: 640px) {
  .contact-page__inner {
    padding-inline: var(--space-4);
  }

  .contact-page__title {
    font-size: 22px;
  }

  .contact-page__row {
    grid-template-columns: 1fr;
  }
}
</style>
